import type {Chart, SVGElement} from 'highcharts';

const LABEL_COLOR = '#909090';
const PADDING = 6;
const FONT_SIZE = 10;

export interface QuadrantLabels {
    topLeft: string;
    topRight: string;
    bottomLeft: string;
    bottomRight: string;
}

type ChartWithQuadrants = Chart & {dashboardQuadrants?: SVGElement};

/** Subtle corner captions; redrawn on every render because plot area size changes with resize/legend toggles. */
export function drawQuadrantLabels(chart: Chart, labels: QuadrantLabels): void {
    const target = chart as ChartWithQuadrants;
    target.dashboardQuadrants?.destroy();

    const {plotLeft, plotTop, plotWidth, plotHeight, renderer} = chart;
    const group = renderer.g('dashboard-quadrants').attr({zIndex: 1}).add();
    const corners: [string, number, number, 'left' | 'right', boolean][] = [
        [labels.topLeft, plotLeft + PADDING, plotTop + PADDING + FONT_SIZE, 'left', false],
        [labels.topRight, plotLeft + plotWidth - PADDING, plotTop + PADDING + FONT_SIZE, 'right', false],
        [labels.bottomLeft, plotLeft + PADDING, plotTop + plotHeight - PADDING, 'left', true],
        [labels.bottomRight, plotLeft + plotWidth - PADDING, plotTop + plotHeight - PADDING, 'right', true],
    ];
    // Each caption gets half the plot, otherwise long variable names in neighbouring corners run into each other
    const width = Math.max(plotWidth / 2 - 2 * PADDING, 0);
    corners.forEach(([text, x, y, align, bottom]) => {
        const label = renderer.text(text, x, y)
            .attr({align})
            .css({color: LABEL_COLOR, fontSize: `${FONT_SIZE}px`, pointerEvents: 'none', width: `${width}px`, textOverflow: 'ellipsis', whiteSpace: 'nowrap'})
            .add(group);
        // y is the first line's baseline, so a multi-line bottom caption has to be lifted by the lines below it
        if (bottom) {
            label.attr({y: y - (label.getBBox().height - FONT_SIZE * 1.2)});
        }
    });
    target.dashboardQuadrants = group;
}
